import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Save, Loader2, Eye, AlertTriangle, Link2, Copy } from "lucide-react";

const TPL_LABELS = {
  pianificazione: "Template Pianificazione (primo invio appuntamento)",
  variazione: "Template Variazione (cambio data appuntamento)",
};

const SAMPLE_CTX = {
  nome: "Mario Rossi",
  data: "12/06/2026",
  ora: "08:30",
  squadra: "Squadra Verde",
  indirizzo: "Via Roma 1, Milano, 20100",
  data_vecchia: "05/06/2026 alle 09:00",
  data_nuova: "12/06/2026 alle 08:30",
};

function renderLocal(text, ctx) {
  let out = text || "";
  for (const [k, v] of Object.entries(ctx)) out = out.replaceAll(`{{${k}}}`, v);
  return out;
}

export default function WhatsappSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [previewTab, setPreviewTab] = useState("pianificazione");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/whatsapp");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updateTemplate = (k, v) => setData((d) => ({ ...d, templates: { ...d.templates, [k]: v } }));
  const updateSid = (k, v) => setData((d) => ({ ...d, templates_sids: { ...d.templates_sids, [k]: v } }));

  const insertPlaceholder = (tplKey, ph) => {
    const ta = document.getElementById(`tpl-${tplKey}`);
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const cur = data.templates[tplKey] || "";
    const next = cur.slice(0, start) + ph + cur.slice(end);
    updateTemplate(tplKey, next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + ph.length, start + ph.length); }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!data.enabled,
        account_sid: data.account_sid || "",
        auth_token: newToken,
        from_number: data.from_number || "",
        default_country_code: data.default_country_code || "+39",
        templates: data.templates || {},
        templates_sids: data.templates_sids || {},
      };
      const { data: out } = await api.put("/settings/whatsapp", payload);
      setData(out);
      setNewToken("");
      toast.success("Impostazioni WhatsApp salvate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato negli appunti");
  };

  if (loading || !data) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="page-whatsapp-settings">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl" style={{ background: "rgba(37,211,102,0.12)" }}>
          <MessageCircle className="w-6 h-6" style={{ color: "#25D366" }} />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Impostazioni WhatsApp</h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Twilio WhatsApp Business · template e credenziali</p>
        </div>
      </div>

      {data.mocked && (
        <div className="p-4 rounded-lg flex items-start gap-3" style={{ background: "#FEF3C7", border: "1px solid #FCD34D" }}>
          <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: "#92400E" }} />
          <div className="text-sm" style={{ color: "#92400E" }}>
            <div className="font-semibold">Modalità MOCK attiva</div>
            <div className="mt-1">Twilio non è configurato (manca SID/Auth Token o "Abilitato" è OFF). I messaggi vengono salvati nel CRM con status "sent" ma <strong>non spediti realmente</strong>. Compila i campi sotto per attivare l'invio reale.</div>
          </div>
        </div>
      )}

      {/* Credenziali Twilio */}
      <div className="card-shell p-5">
        <h2 className="font-bold mb-4" style={{ fontFamily: "Manrope" }}>Credenziali Twilio</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 cursor-pointer p-3 rounded-md border" style={{ borderColor: "var(--border)" }}>
            <input type="checkbox" checked={!!data.enabled} onChange={(e) => updateField("enabled", e.target.checked)} data-testid="wa-enabled" />
            <span className="text-sm font-semibold">Abilita invio WhatsApp</span>
          </label>
          <Field
            label="Account SID (ACxxxx…)"
            value={data.account_sid}
            onChange={(v) => updateField("account_sid", v)}
            placeholder="AC0123456789abcdef0123456789abcdef"
            testid="wa-account-sid"
          />
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Auth Token {data.auth_token_set && <span className="text-green-700">· salvato ✓</span>}
            </label>
            <div className="relative mt-1">
              <input
                type={showAuthToken ? "text" : "password"}
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                placeholder={data.auth_token_set ? "•••••••••• (lascia vuoto per non modificare)" : "Incolla il token Twilio"}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)" }}
                data-testid="wa-auth-token"
              />
              <button type="button" onClick={() => setShowAuthToken(!showAuthToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-800">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </div>
          <Field
            label="Numero mittente WhatsApp (E.164)"
            value={data.from_number}
            onChange={(v) => updateField("from_number", v)}
            placeholder="+393339990001"
            testid="wa-from-number"
          />
          <Field
            label="Prefisso default per numeri senza +"
            value={data.default_country_code}
            onChange={(v) => updateField("default_country_code", v)}
            placeholder="+39"
            testid="wa-default-cc"
          />
        </div>

        {/* Webhooks info */}
        <div className="mt-5 p-4 rounded-md text-sm" style={{ background: "var(--surface-muted)" }}>
          <div className="font-semibold mb-2 flex items-center gap-2"><Link2 className="w-4 h-4" /> Webhook da configurare in Twilio</div>
          <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>{data.webhooks?.instructions}</p>
          <WebhookRow label="Messaggi in arrivo (When a message comes in)" url={data.webhooks?.inbound_url} onCopy={copyToClipboard} />
          <WebhookRow label="Status callback (delivery + read)" url={data.webhooks?.status_url} onCopy={copyToClipboard} />
        </div>
      </div>

      {/* Templates */}
      <div className="card-shell p-5">
        <h2 className="font-bold mb-1" style={{ fontFamily: "Manrope" }}>Template messaggi</h2>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          Usa i placeholder qui sotto: verranno sostituiti automaticamente con i dati del cliente al momento dell'invio. <strong>Per messaggi fuori dalle 24h</strong> dall'ultima interazione del cliente, WhatsApp richiede un <strong>template approvato</strong>: incolla il Content SID nella colonna a destra se ne hai uno.
        </p>

        <div className="flex flex-wrap gap-1 mb-3">
          {(data.supported_placeholders || []).map((p) => (
            <button
              key={p.key}
              onClick={() => insertPlaceholder(previewTab, p.key)}
              className="px-2 py-1 text-[11px] rounded font-mono border"
              style={{ borderColor: "var(--border)", background: "white" }}
              title={p.label}
              data-testid={`wa-placeholder-${p.key.replace(/[{}]/g, "")}`}
            >
              {p.key}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-3 border-b" style={{ borderColor: "var(--border)" }}>
          {Object.keys(TPL_LABELS).map((k) => (
            <button
              key={k}
              onClick={() => setPreviewTab(k)}
              className="px-3 py-2 -mb-px text-sm font-semibold border-b-2"
              style={{
                borderColor: previewTab === k ? "var(--primary)" : "transparent",
                color: previewTab === k ? "var(--primary)" : "var(--text-muted)",
              }}
              data-testid={`wa-tpl-tab-${k}`}
            >
              {TPL_LABELS[k]}
            </button>
          ))}
        </div>

        {Object.keys(TPL_LABELS).map((k) => (
          <div key={k} className={previewTab === k ? "" : "hidden"}>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Testo</label>
                <textarea
                  id={`tpl-${k}`}
                  rows={8}
                  value={data.templates[k] || ""}
                  onChange={(e) => updateTemplate(k, e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none font-mono"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`wa-template-${k}`}
                />
                <div className="flex items-center justify-between mt-1">
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{(data.templates[k] || "").length} caratteri</div>
                  <button
                    onClick={() => updateTemplate(k, data.default_templates[k])}
                    className="text-[11px] underline" style={{ color: "var(--text-muted)" }}
                  >
                    Ripristina default
                  </button>
                </div>
                <div className="mt-3">
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Content SID (opzionale, per template approvati WhatsApp)</label>
                  <input
                    value={data.templates_sids?.[k] || ""}
                    onChange={(e) => updateSid(k, e.target.value)}
                    placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none font-mono"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`wa-content-sid-${k}`}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Anteprima con dati di esempio</label>
                <div className="mt-1 p-3 rounded-lg text-sm whitespace-pre-wrap min-h-[180px]" style={{ background: "#DCF8C6", color: "#0B141A" }} data-testid={`wa-template-preview-${k}`}>
                  {renderLocal(data.templates[k] || "", SAMPLE_CTX)}
                </div>
                <div className="mt-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                  Dati di esempio: <code>{`{nome: ${SAMPLE_CTX.nome}, data: ${SAMPLE_CTX.data}, …}`}</code>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="wa-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
        style={{ borderColor: "var(--border)" }}
        data-testid={testid}
      />
    </div>
  );
}

function WebhookRow({ label, url, onCopy }) {
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <div className="text-xs flex-shrink-0 w-28 sm:w-64" style={{ color: "var(--text-muted)" }}>{label}</div>
      <code className="flex-1 min-w-0 px-2 py-1.5 text-xs rounded font-mono truncate" style={{ background: "white", border: "1px solid var(--border)" }}>
        {url || "—"}
      </code>
      <button onClick={() => url && url !== "—" && onCopy(url)} className="p-1.5 text-gray-500 hover:text-gray-800" disabled={!url || url === "—"}>
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
